// PermissionGrid — internally stateful checkbox grid over PERMISSION_CATALOG.
//
// State strategy:
//   - The grid owns `selected: Set<PermissionCode>`, seeded once from
//     `initial` on mount via `useState(() => new Set(initial))`. The parent
//     (`RoleEditDrawer`) keys the grid by `data.permsVersion` so a successful
//     save or stale-409 invalidation forces a remount and the grid re-seeds
//     against the fresh server snapshot — no fully-controlled handoff.
//   - `onDirtyChange(isDirty)` fires whenever `selected` diverges from /
//     converges back to `initial` (set equality). The parent
//     `RoleEditDrawer` lifts that signal into its shared
//     `useUnsavedChangesGuard` so unsaved permission toggles can't be
//     dropped silently on close / tab switch.
//
// Co-grant rules (server enforces; UI mirrors so admin doesn't have to hunt):
//   - `users.manage` requires both `roles.read` and `departments.read`
//   - `departments.manage` requires `departments.read`
//   - `roles.manage` requires `roles.read`
// On ErrorCode.MISSING_CO_GRANT we auto-tick the missing companion(s) for
// whichever .manage code is currently selected, then surface the error so
// the admin can resubmit. Single-round recovery, never an infinite loop.
//
// Catalog is grouped by `<resource>` prefix; transitions get their own
// section because they're not navigation rights.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSION_CATALOG, type PermissionCode, ErrorCode } from '@magimp/shared';
import { Button } from '../../../ui/index.js';
import { ApiClientError } from '../../../api/errors.js';
import { permissionGroupKey, permissionLabelKey, type RbacGroupId } from '../../../i18n/index.js';

export interface PermissionGridProps {
  initial: readonly PermissionCode[];
  disabled?: boolean;
  pending?: boolean;
  onSubmit: (codes: PermissionCode[]) => Promise<unknown>;
  onError?: (err: unknown) => void;
  /**
   * Fires with `true` when the local `selected` set diverges from `initial`,
   * and with `false` when it converges back (set equality). Used by
   * `RoleEditDrawer` to lift permissions-tab dirtiness into the shared
   * unsaved-changes guard without giving up the grid's internal state.
   * The grid does not store form state in any parent.
   */
  onDirtyChange?: (isDirty: boolean) => void;
}

type Group = { id: string; label: RbacGroupId; codes: readonly PermissionCode[] };

function buildGroups(): Group[] {
  const navigation = new Map<RbacGroupId, PermissionCode[]>();
  const transitions: PermissionCode[] = [];
  for (const entry of PERMISSION_CATALOG) {
    if (entry.code.includes('.transition.')) {
      transitions.push(entry.code);
      continue;
    }
    // WHY: PermissionCode is `<resource>.<verb>...`, so split[0] is exactly
    // the RbacGroupId resource component the locale tree expects.
    const resource = entry.code.split('.')[0] as RbacGroupId;
    const list = navigation.get(resource) ?? [];
    list.push(entry.code);
    navigation.set(resource, list);
  }
  const groups: Group[] = [];
  for (const [resource, codes] of navigation) {
    groups.push({
      id: resource,
      label: resource,
      codes,
    });
  }
  if (transitions.length > 0) {
    groups.push({ id: 'transitions', label: 'transitions', codes: transitions });
  }
  return groups;
}

export function PermissionGrid({
  initial,
  disabled = false,
  pending = false,
  onSubmit,
  onError,
  onDirtyChange,
}: PermissionGridProps) {
  const { t } = useTranslation(['rbac', 'common']);
  const groups = useMemo(buildGroups, []);
  const [selected, setSelected] = useState<Set<PermissionCode>>(() => new Set(initial));

  // Seed-baseline against `initial` captured on mount. The parent remounts
  // the grid by `key={data.permsVersion}` on every successful save / stale
  // refetch, so the baseline is always fresh — there is no need to track
  // `initial` over the grid's lifetime.
  const baselineRef = useRef<Set<PermissionCode>>(new Set(initial));
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);
  // Bubble `false` on unmount so a tab switch / drawer close doesn't leave
  // the parent guard holding a stale-dirty signal for a grid that no longer
  // exists. Mirrors the `ProfileForm` pattern in UserEditDrawer.
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  const reportDirty = (next: Set<PermissionCode>): void => {
    const base = baselineRef.current;
    let dirty = next.size !== base.size;
    if (!dirty) {
      for (const code of next) {
        if (!base.has(code)) {
          dirty = true;
          break;
        }
      }
    }
    onDirtyChangeRef.current?.(dirty);
  };

  const toggle = (code: PermissionCode) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      reportDirty(next);
      return next;
    });
  };

  const submit = async () => {
    try {
      await onSubmit(Array.from(selected));
    } catch (err) {
      if (err instanceof ApiClientError && err.code === ErrorCode.MISSING_CO_GRANT) {
        // Auto-add the missing co-grant(s) so the admin doesn't have to find
        // them. We can't read which permission was flagged from
        // ApiClientError today, so add the universe of companions for
        // whichever .manage code is currently selected. Set semantics dedupe.
        setSelected((prev) => {
          const next = new Set(prev);
          if (prev.has('users.manage')) {
            next.add('roles.read');
            next.add('departments.read');
          }
          if (prev.has('departments.manage')) {
            next.add('departments.read');
          }
          if (prev.has('roles.manage')) {
            next.add('roles.read');
          }
          reportDirty(next);
          return next;
        });
      }
      onError?.(err);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {groups.map((group) => (
          <fieldset
            key={group.id}
            style={{
              border: '1px solid var(--border-2)',
              borderRadius: 'var(--r-1)',
              padding: 8,
              margin: 0,
            }}
          >
            <legend style={{ fontSize: 12, color: 'var(--fg-2)', padding: '0 4px' }}>
              {t(permissionGroupKey(group.label))}
            </legend>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: 6,
              }}
            >
              {group.codes.map((code) => {
                const checked = selected.has(code);
                return (
                  <label
                    key={code}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 13,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={() => toggle(code)}
                    />
                    <span>{t(permissionLabelKey(code))}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        ))}
      </div>
      {!disabled && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button kind="primary" onClick={() => void submit()} disabled={pending}>
            {t('actions.savePermissions')}
          </Button>
        </div>
      )}
    </div>
  );
}
